import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './Recipes.css';

const EMPTY_FORM = { title: '', ingredients: '', instructions: '', is_shared: false };

function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

const Recipes = () => {
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [detailRecipe, setDetailRecipe] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const fetchRecipes = useCallback(async () => {
        setError('');
        try {
            const r = await fetch('/api/recipes', { credentials: 'include' });
            if (!r.ok) throw new Error('Failed to load recipes');
            setRecipes(await r.json());
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRecipes(); }, [fetchRecipes]);

    const myRecipes = useMemo(() => recipes.filter(r => r.is_owner), [recipes]);
    const sharedRecipes = useMemo(() => recipes.filter(r => !r.is_owner), [recipes]);

    const openForm = (recipe = null) => {
        setDetailRecipe(null);
        if (recipe) {
            setEditingId(recipe.id);
            setForm({
                title: recipe.title,
                ingredients: recipe.ingredients || '',
                instructions: recipe.instructions || '',
                is_shared: Boolean(recipe.is_shared),
            });
        } else {
            setEditingId(null);
            setForm(EMPTY_FORM);
        }
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm(EMPTY_FORM);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return;

        setError('');
        setSaving(true);
        try {
            const r = await fetch(editingId ? `/api/recipes/${editingId}` : '/api/recipes', {
                method: editingId ? 'PATCH' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(form),
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || 'Failed to save recipe');
            closeForm();
            await fetchRecipes();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (recipe) => {
        if (!window.confirm(`Delete "${recipe.title}"? This cannot be undone.`)) return;
        setError('');
        setDeletingId(recipe.id);
        try {
            const r = await fetch(`/api/recipes/${recipe.id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!r.ok) {
                const data = await r.json();
                throw new Error(data.error || 'Failed to delete recipe');
            }
            setRecipes(prev => prev.filter(rc => rc.id !== recipe.id));
            if (detailRecipe?.id === recipe.id) setDetailRecipe(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setDeletingId(null);
        }
    };

    const renderCard = (recipe) => (
        <li key={recipe.id} className="recipe-card">
            <button
                type="button"
                className="recipe-card-open"
                onClick={() => { setShowForm(false); setDetailRecipe(recipe); }}
            >
                <h3>{recipe.title}</h3>
                <div className="recipe-card-meta">
                    {!recipe.is_owner && recipe.owner_username && (
                        <span>by {recipe.owner_username}</span>
                    )}
                    {recipe.is_owner && recipe.is_shared && (
                        <span className="recipe-badge recipe-badge-shared">Shared</span>
                    )}
                    <span>Updated {formatDate(recipe.updated_at)}</span>
                </div>
            </button>
            {recipe.is_owner && (
                <div className="recipe-card-actions">
                    <button type="button" onClick={() => openForm(recipe)}>Edit</button>
                    <button
                        type="button"
                        className="recipe-btn-danger"
                        onClick={() => handleDelete(recipe)}
                        disabled={deletingId === recipe.id}
                    >
                        {deletingId === recipe.id ? '…' : 'Delete'}
                    </button>
                </div>
            )}
        </li>
    );

    return (
        <div className="recipes">
            <div className="recipes-header">
                <h1>Recipes</h1>
                {!showForm && (
                    <button type="button" className="recipe-btn-primary" onClick={() => openForm()}>
                        New recipe
                    </button>
                )}
            </div>

            {error && <div className="alert-error">{error}</div>}

            {showForm && (
                <form className="recipe-form" onSubmit={handleSave}>
                    <h2>{editingId ? 'Edit recipe' : 'New recipe'}</h2>
                    <label>
                        Title
                        <input
                            type="text"
                            value={form.title}
                            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                            maxLength={300}
                            required
                            disabled={saving}
                        />
                    </label>
                    <label>
                        Ingredients
                        <textarea
                            value={form.ingredients}
                            onChange={e => setForm(f => ({ ...f, ingredients: e.target.value }))}
                            placeholder={'One ingredient per line, e.g.\n2 cups flour\n1 tsp salt'}
                            disabled={saving}
                        />
                    </label>
                    <label>
                        Instructions
                        <textarea
                            className="recipe-form-instructions"
                            value={form.instructions}
                            onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))}
                            placeholder="Steps to prepare the recipe…"
                            disabled={saving}
                        />
                    </label>
                    <label className="recipe-form-share">
                        <input
                            type="checkbox"
                            checked={form.is_shared}
                            onChange={e => setForm(f => ({ ...f, is_shared: e.target.checked }))}
                            disabled={saving}
                        />
                        Share this recipe with other members
                    </label>
                    <div className="recipe-form-actions">
                        <button type="submit" className="recipe-btn-primary" disabled={saving || !form.title.trim()}>
                            {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add recipe'}
                        </button>
                        <button type="button" onClick={closeForm} disabled={saving}>
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            {detailRecipe && !showForm && (
                <div className="recipe-detail">
                    <button type="button" className="recipe-detail-back" onClick={() => setDetailRecipe(null)}>
                        ← Back to list
                    </button>
                    <div className="recipe-detail-header">
                        <div>
                            <h2>{detailRecipe.title}</h2>
                            <div className="recipe-card-meta">
                                {detailRecipe.owner_username && <span>by {detailRecipe.owner_username}</span>}
                                {detailRecipe.is_shared && (
                                    <span className="recipe-badge recipe-badge-shared">Shared</span>
                                )}
                                <span>Updated {formatDate(detailRecipe.updated_at)}</span>
                            </div>
                        </div>
                        {detailRecipe.is_owner && (
                            <div className="recipe-card-actions">
                                <button type="button" onClick={() => openForm(detailRecipe)}>Edit</button>
                                <button
                                    type="button"
                                    className="recipe-btn-danger"
                                    onClick={() => handleDelete(detailRecipe)}
                                    disabled={deletingId === detailRecipe.id}
                                >
                                    {deletingId === detailRecipe.id ? '…' : 'Delete'}
                                </button>
                            </div>
                        )}
                    </div>
                    {detailRecipe.ingredients && (
                        <section>
                            <h3>Ingredients</h3>
                            <pre className="recipe-text">{detailRecipe.ingredients}</pre>
                        </section>
                    )}
                    {detailRecipe.instructions && (
                        <section>
                            <h3>Instructions</h3>
                            <pre className="recipe-text">{detailRecipe.instructions}</pre>
                        </section>
                    )}
                    {!detailRecipe.ingredients && !detailRecipe.instructions && (
                        <p className="recipes-empty">This recipe has no details yet.</p>
                    )}
                </div>
            )}

            {!detailRecipe && !showForm && (
                loading ? (
                    <p>Loading recipes…</p>
                ) : (
                    <>
                        <section>
                            <h2 className="recipes-section-title">My recipes</h2>
                            {myRecipes.length === 0 ? (
                                <p className="recipes-empty">
                                    No recipes yet — use “New recipe” to add your first one.
                                </p>
                            ) : (
                                <ul className="recipe-list">{myRecipes.map(renderCard)}</ul>
                            )}
                        </section>
                        <section>
                            <h2 className="recipes-section-title">Shared by others</h2>
                            {sharedRecipes.length === 0 ? (
                                <p className="recipes-empty">No shared recipes from other members yet.</p>
                            ) : (
                                <ul className="recipe-list">{sharedRecipes.map(renderCard)}</ul>
                            )}
                        </section>
                    </>
                )
            )}
        </div>
    );
};

export default Recipes;
